import { AppointmentTypeDto } from '@shared/core/appointment-types'
import { SlotGenerationResult } from '@shared/core/availabilities'
import { ProviderDetailsDto } from '@shared/core/bookings'
import ms from 'ms'
import { useQuery, UseQueryOptions } from 'react-query'
import { useHandleRetry } from 'src/modules/core/hooks/use-handle-retry'
import { AppointmentTypesService } from 'src/services/appointment-types.service'
import { AvailabilitySlotsService } from 'src/services/availability-slots.service'
import { BookingsService } from 'src/services/bookings.service'
import { ensureUserIdPrefix } from '../utils/user-id'

// Cache keys
export const COMPUTE_SLOTS_FOR_CUSTOMER_DAY_CACHE_KEY = 'compute-slots-for-customer-day'
export const GET_APPOINTMENT_TYPES_CACHE_KEY = 'get-appointment-types'
export const GET_PROVIDER_DETAILS_CACHE_KEY = 'get-provider-details'

// Compute slots for customer day (customer timezone)
export function useComputeSlotsForCustomerDay(
  params: {
    providerId: string
    appointmentTypeId: string
    customerDateISO?: string
    customerTz: string
    rescheduleCode?: string
  },
  options?: UseQueryOptions<SlotGenerationResult>,
) {
  const handleRetry = useHandleRetry()
  return useQuery(
    [
      COMPUTE_SLOTS_FOR_CUSTOMER_DAY_CACHE_KEY,
      params.providerId,
      params.appointmentTypeId,
      params.customerDateISO,
      params.customerTz,
      params.rescheduleCode,
    ],
    () =>
      AvailabilitySlotsService.computeSlotsForCustomerDay({
        ...params,
        providerId: ensureUserIdPrefix(params.providerId),
      }),
    {
      enabled: !!params.providerId && !!params.appointmentTypeId && !!params.customerTz,
      staleTime: ms('5 minutes'),
      retry: handleRetry,
      ...options,
    },
  )
}

// Get appointment types for a provider
export function useAppointmentTypes(providerId: string, options?: UseQueryOptions<AppointmentTypeDto[]>) {
  const handleRetry = useHandleRetry()
  return useQuery(
    [GET_APPOINTMENT_TYPES_CACHE_KEY, providerId],
    () => AppointmentTypesService.list(ensureUserIdPrefix(providerId)),
    {
      enabled: !!providerId,
      staleTime: ms('5 minutes'),
      retry: handleRetry,
      ...options,
    },
  )
}

// Get provider name (with aggressive caching since it's unlikely to change)
export function useProviderDetails(providerId: string, options?: UseQueryOptions<ProviderDetailsDto>) {
  const handleRetry = useHandleRetry()
  return useQuery(
    [GET_PROVIDER_DETAILS_CACHE_KEY, providerId],
    () => BookingsService.getProviderDetails(ensureUserIdPrefix(providerId)),
    {
      enabled: !!providerId,
      staleTime: ms('24 hours'), // Aggressive caching - provider name rarely changes
      cacheTime: ms('7 days'), // Keep in cache for 7 days
      retry: handleRetry,
      ...options,
    },
  )
}
